/* eslint-disable consistent-return */
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import { FieldErrors, FormProvider, useForm } from "react-hook-form";

import LabelInputRadioBox from "./LabelInputRadioBox";
import ImageItem from "./ImageItem";
import { DefaultBtn } from "common/button";
import { LabelInput } from "common/input";
import { Dialog, ErrorModal, LoadingModal } from "common/modal";
import { Content } from "common/container";
import { Footer, InfoItem } from "common/other";
import queryKey from "constants/queryKey";
import { updateCompanyData } from "api/company";
import { uploadCommonImgApi } from "api/common";
import { useAccessRole, useModal } from "hooks";
import { TCompany, TCompanyInput, TCompanyPrivacyPolicyInfo } from "types/common";
import { TGetAccessRole } from "types/login";

import styles from "style/pages/management/company/companyEditor.module.scss";

export default function CompanyEditor({ data }: { data: TCompany }) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const accessInfo = useAccessRole().data as TGetAccessRole;

  const [isNullErrorModal, setIsNullErrorModal] = useModal();
  const [isErrorShowing, setIsErrorShowing] = useModal();
  const [isUpdateModal, setIsUpdateModal] = useModal();
  const [isConfirmModal, setIsConfirmModal] = useModal();
  const [errorMsg, setErrorMsg] = useState("");

  const privacyPolicyInfo: TCompanyPrivacyPolicyInfo = JSON.parse(data.privacyPolicyInfo || "{}");

  const formatBankAccount = (bankAccountNumber: string, idx: number) => {
    return (bankAccountNumber || "").split(",")[idx];
  };

  const defaultValues = {
    businessNumber: data.businessNumber,
    businessHeadOfficeAddress: data.businessHeadOfficeAddress,
    businessRepresentativeName: data.businessRepresentativeName,
    tel: data.tel,
    privacyPolicyUrl: data.privacyPolicyUrl,
    businessRegistrationImageUrl: data?.businessRegistrationImageUrl,
    privacyPolicyOfficer: privacyPolicyInfo.privacyPolicyOfficer as string,
    privacyPolicyOfficerTel: privacyPolicyInfo.privacyPolicyOfficerTel as string,
    problemHandlingDept: privacyPolicyInfo.problemHandlingDept as string,
    problemHandlingDeptTel: privacyPolicyInfo.problemHandlingDeptTel as string,
    bankName: formatBankAccount(data?.bankAccountNumber!, 0),
    accountHolderName: formatBankAccount(data?.bankAccountNumber!, 1),
    accountNumber: formatBankAccount(data?.bankAccountNumber!, 2),
    businessDigitalTaxInvoiceEmail: data.businessDigitalTaxInvoiceEmail,
    homepageUrl: data.homepageUrl,
    bankbookCopyImageUrl: data?.bankbookCopyImageUrl,
    logoUrl: data?.logoUrl,
  };

  const methods = useForm<TCompanyInput>({ mode: "onChange", defaultValues });

  const {
    handleSubmit,
    formState: { errors },
    getValues,
  } = methods;

  // 기업 정보 수정 API 연동
  const { mutate: updateMutation, error: updateError, isLoading } = useMutation(updateCompanyData);
  const { mutateAsync: uploadImgMutation, error: uploadImgError } = useMutation(uploadCommonImgApi);

  const handleClickConfirm = () => {
    queryClient.invalidateQueries([queryKey.GET_DETAIL_COMPANY]);
    queryClient.invalidateQueries([queryKey.GET_COMPANY]);
    navigate(`/management/company/info/${id}`);
  };

  const handleErrorModal = (error: FieldErrors) => {
    if (error) {
      const errorMessages = Object.values(error).filter((v) => v?.message);

      if (errorMessages.length) {
        setErrorMsg(String(errorMessages[0]?.message));
      } else if (
        getValues(["businessRegistrationImageUrl", "bankbookCopyImageUrl", "logoUrl"]).map((el) => el === null)
      ) {
        setErrorMsg("파일을 업로드해 주세요.");
      } else {
        setErrorMsg("알 수 없는 오류가 발생했습니다.");
      }
    }
    setIsNullErrorModal();
  };

  const handleUpdate = async (input: TCompanyInput) => {
    const formatImg = async (file: File | string) => {
      if (!accessInfo.did || !accessInfo.token) return "";

      const typeFile = file as File;
      const body = { did: accessInfo.did, token: accessInfo.token, file: typeFile };

      const imgLink =
        typeof file === "string"
          ? file
          : await uploadImgMutation(body, {
              onError: () => {
                setIsConfirmModal();
                setIsErrorShowing();
              },
            }).then((el) => el.fullUrlPath);

      return imgLink;
    };

    const body = {
      id,
      name: data.name,
      businessNumber: input.businessNumber,
      businessHeadOfficeAddress: input.businessHeadOfficeAddress,
      tel: input.tel,
      businessRepresentativeName: input.businessRepresentativeName,
      businessRegistrationImageUrl: await formatImg(input.businessRegistrationImageUrl),
      bankbookCopyImageUrl: await formatImg(input.bankbookCopyImageUrl),
      logoUrl: await formatImg(input.logoUrl),
      bankAccountNumber: [input.bankName, input.accountHolderName, input.accountNumber].join(","),
      businessType: data.businessType || "", // 소재지
      piiUrl: data.piiUrl, // 데이터 수집 서버 URL
      privacyPolicyUrl: input.privacyPolicyUrl, // 개인정보 처리방침 URL
      businessDigitalTaxInvoiceEmail: input.businessDigitalTaxInvoiceEmail, // 전자세금계산서용 이메일 주소
      businessUnitTaxation: input.businessUnitTaxation, // 사업자 단위 과세 적용사업자 여부
      homepageUrl: input.homepageUrl ?? "", // 홈페이지 URL
      privacyPolicyInfo: JSON.stringify({
        privacyPolicyOfficer: input.privacyPolicyOfficer, // 개인정보 보호 책임자 명
        privacyPolicyOfficerTel: input.privacyPolicyOfficerTel, // 개인정보 보호 책임자 연락처
        problemHandlingDept: input.problemHandlingDept, // 고충처리 담당부서 명
        problemHandlingDeptTel: input.problemHandlingDeptTel, // 고충처리 담당부서 연락처
      }),
    };

    setIsConfirmModal();
    updateMutation(body, {
      onSuccess: setIsUpdateModal,
      onError: () => {
        setIsConfirmModal();
        setIsErrorShowing();
      },
    });
  };

  if (isLoading) return <LoadingModal />;

  return (
    <Content>
      <FormProvider {...methods}>
        <form className={styles.contentContainer} onSubmit={handleSubmit(setIsConfirmModal, handleErrorModal)}>
          <InfoItem labelWidth="290px" label="기업명" info={data.name} />
          <InfoItem labelWidth="290px" label="소재지" info={data.businessType === "1" ? "해외" : "국내"} />
          <InfoItem labelWidth="290px" label="기업 DID" info={data.did} />
          <InfoItem labelWidth="290px" label="데이터 수집 서버 URL" info={data.piiUrl} />

          <ImageItem
            id="businessRegistrationImageUrl"
            accept=".pdf, .png, .jpg, .jpeg"
            sizeLimit={20}
            labelName="사업자등록증"
          />

          <LabelInput
            labelWidth="290px"
            label="사업자등록번호"
            id="businessNumber"
            placeholder="'-'를 제외한 숫자만 입력하세요."
            isNull={!!errors.businessNumber}
            type="number"
            validate="businessNumber"
          />

          <LabelInput
            labelWidth="290px"
            label="주소"
            id="businessHeadOfficeAddress"
            placeholder="주소를 입력하세요"
            isNull={!!errors.businessHeadOfficeAddress}
            maxLength={150}
          />

          <LabelInput
            labelWidth="290px"
            label="대표자명"
            id="businessRepresentativeName"
            placeholder="대표자명을 입력하세요"
            isNull={!!errors.businessRepresentativeName}
            maxLength={50}
          />

          <LabelInput
            labelWidth="290px"
            label="대표번호"
            id="tel"
            placeholder="'-'를 제외한 숫자만 입력하세요"
            isNull={!!errors.tel}
            type="number"
            validate="tel"
          />

          <LabelInput
            labelWidth="290px"
            label="개인정보 처리방침 URL"
            id="privacyPolicyUrl"
            placeholder="생성하려는 기업의 개인정보처리방침 페이지 주소를 입력하세요"
            isNull={!!errors.privacyPolicyUrl}
            validate="url"
            maxLength={200}
          />

          <LabelInput
            labelWidth="290px"
            label="개인정보 보호 책임자명"
            id="privacyPolicyOfficer"
            placeholder="개인정보 보호 책임자명을 입력하세요"
            isNull={!!errors.privacyPolicyOfficer}
            maxLength={50}
          />

          <LabelInput
            labelWidth="290px"
            label="개인정보 보호 책임자 연락처"
            id="privacyPolicyOfficerTel"
            placeholder="개인정보 보호 책임자 혹은 담당 부서의 연락처를 입력하세요"
            isNull={!!errors.privacyPolicyOfficerTel}
            type="number"
            validate="tel"
          />

          <LabelInput
            labelWidth="290px"
            label="고충처리 담당부서명"
            id="problemHandlingDept"
            placeholder="고객 고충처리 담당부서명을 입력하세요"
            isNull={!!errors.problemHandlingDept}
            maxLength={50}
          />

          <LabelInput
            labelWidth="290px"
            label="고충처리 담당부서 연락처"
            id="problemHandlingDeptTel"
            placeholder="고충처리 담당부서 연락처를 입력하세요"
            isNull={!!errors.problemHandlingDeptTel}
            type="number"
            validate="tel"
          />

          <ImageItem id="bankbookCopyImageUrl" accept=".pdf, .png, .jpg, .jpeg" sizeLimit={20} labelName="통장 사본" />

          <LabelInput
            labelWidth="290px"
            label="은행명"
            id="bankName"
            placeholder="은행명을 입력하세요"
            isNull={!!errors.bankName}
            maxLength={30}
          />

          <LabelInput
            labelWidth="290px"
            label="예금주명"
            id="accountHolderName"
            placeholder="예금주명을 입력하세요"
            isNull={!!errors.accountHolderName}
            maxLength={50}
          />

          <LabelInput
            labelWidth="290px"
            label="계좌번호"
            id="accountNumber"
            placeholder="계좌번호를 입력하세요"
            isNull={!!errors.accountNumber}
            type="number"
            validate="numberLength20"
          />

          <LabelInput
            labelWidth="290px"
            label="전자세금계산서용 이메일 주소"
            id="businessDigitalTaxInvoiceEmail"
            placeholder="전자세금계산서 발행에 사용할 이메일 주소를 입력하세요"
            isNull={!!errors.businessDigitalTaxInvoiceEmail}
            validate="email"
            maxLength={100}
          />

          <LabelInputRadioBox
            label="사업자 단위 과세 적용사업자 여부"
            labelWidth="290px"
            options={[
              {
                id: "unitTaxationOn",
                value: true,
                label: "여",
                DefaultValue: data?.businessUnitTaxation,
              },
              {
                id: "unitTaxationOff",
                value: false,
                label: "부",
                DefaultValue: !data?.businessUnitTaxation,
              },
            ]}
            id="businessUnitTaxation"
            isNull={!!errors.businessUnitTaxation}
          />

          <LabelInput
            labelWidth="290px"
            label="홈페이지 URL"
            id="homepageUrl"
            validate="url"
            placeholder="기업 홈페이지 주소를 입력하세요"
            must={false}
            maxLength={512}
          />

          <ImageItem id="logoUrl" accept=".png, .jpg, .jpeg" labelName="기업 로고" />

          <Footer>
            <DefaultBtn type="button" size="medium" mode="lineGray" onClick={() => navigate("/management")}>
              목록으로
            </DefaultBtn>
            <DefaultBtn type="submit" size="medium">
              수정
            </DefaultBtn>
          </Footer>

          {/* Modals */}
          {isNullErrorModal && <Dialog description={errorMsg} btnType="confirm" handleClose={setIsNullErrorModal} />}

          {isConfirmModal && (
            <Dialog
              title=""
              description="기업 정보를 수정하시겠습니까?"
              btnType="submit"
              handleClose={setIsConfirmModal}
              handleSubmit={handleSubmit(handleUpdate, handleErrorModal)}
            />
          )}

          {isUpdateModal && (
            <Dialog description="기업 정보가 수정되었습니다." btnType="confirm" handleClose={handleClickConfirm} />
          )}

          {isErrorShowing && <ErrorModal error={updateError || uploadImgError} onConfirmHandler={setIsErrorShowing} />}
        </form>
      </FormProvider>
    </Content>
  );
}
